/**
 * Web App Authentication Service using Auth0 SPA SDK
 * 
 * This module provides authentication for the standalone web portal using
 * the Auth0 SPA SDK instead of Auth0 Lock. This avoids CDN loading issues
 * and provides a more modern, flexible authentication experience.
 */

import { Auth0Client, type Auth0ClientOptions } from "@auth0/auth0-spa-js";
import type { Auth0IdTokenClaims, StoredExtensionAuth } from "../types/api";
import type { Auth0User } from "../types/core/auth";
import { createSessionStorageAdapter, buildAccountFromClaims } from "./authShared";
import {
  getApiBaseUrl,
  getAuth0Audience,
  getAuth0Domain,
  getAuth0Scope,
  getPortalClientId,
} from "../config/auth";
import { createLogger } from "../shared/logger";

const log = createLogger('services:webapp-pkce-auth');

const AUTH_STORAGE_KEY = "lotlingo.webapp.auth";
const API_BASE_URL = getApiBaseUrl();
const AUTH0_DOMAIN = getAuth0Domain();
const AUTH0_AUDIENCE = getAuth0Audience();
const AUTH0_SCOPE = getAuth0Scope();

const TOKEN_COOKIE_NAME = "lotlingo_extension_access";

let auth0Client: Auth0Client | null = null;

/**
 * Resolve the API base URL for backend calls.
 *
 * The portal is deployed to multiple environments (prod, staging) that share a
 * single build but are served different runtime config.json values. The runtime
 * config's apiBaseUrl is the source of truth for the current environment, so we
 * prefer it and only fall back to the build-time default when it is absent.
 */
function resolveApiBaseUrl(override?: string): string {
  const trimmed = typeof override === "string" ? override.trim() : "";
  return (trimmed || API_BASE_URL).replace(/\/$/, "");
}

function ensureBrowserEnvironment(): void {
  if (typeof window === "undefined" || typeof document === "undefined") {
    // In test environments, log a warning instead of throwing to allow
    // module selection tests and spies to execute without hard crashes.
    if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") {
      log.warn("[webapp-auth] Skipping strict browser check in test environment.");
      return;
    }
    throw new Error("Authentication can only be used within a browser environment.");
  }
}

function resolveAuth0ClientId(): string {
  const portalClientId = getPortalClientId();
  if (portalClientId) {
    return portalClientId;
  }

  const isTestEnv = typeof process !== "undefined" && process.env?.NODE_ENV === "test";
  if (isTestEnv) {
    log.warn(
      "[webapp-auth] Using test fallback Auth0 client id because no client id is configured."
    );
    return "TEST_CLIENT_ID";
  }

  throw new Error(
    "Auth0 client ID is not configured. Provide VITE_PORTAL_AUTH0_CLIENT_ID or VITE_EXTENSION_AUTH0_CLIENT_ID before building the standalone web app."
  );
}

async function getAuth0Client(): Promise<Auth0Client> {
  ensureBrowserEnvironment();

  if (auth0Client) {
    return auth0Client;
  }

  const clientId = resolveAuth0ClientId();
  
  const options: Auth0ClientOptions = {
    domain: AUTH0_DOMAIN,
    clientId,
    authorizationParams: {
      redirect_uri: window.location.origin,
      audience: AUTH0_AUDIENCE,
      scope: AUTH0_SCOPE,
    },
    cacheLocation: "memory", // Keep Auth0 SDK token cache in-memory to reduce token exposure surface
    useRefreshTokens: true,
    useRefreshTokensFallback: true, // Fallback to refresh tokens when cache is not available
  };

  auth0Client = new Auth0Client(options);
  return auth0Client;
}

// Shared persistence adapter
const _persist = createSessionStorageAdapter(AUTH_STORAGE_KEY);
const persistAuth = (auth: StoredExtensionAuth) => _persist.save(auth);
const clearPersistedAuth = () => _persist.clear();
const loadPersistedAuth = () => _persist.load();

function readCookie(name: string): string | undefined {
  if (typeof document === "undefined") return undefined;
  const cookieString = document.cookie || "";
  const parts = cookieString.split(";");
  for (const part of parts) {
    const trimmed = part.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return decodeURIComponent(trimmed.slice(name.length + 1));
    }
  }
  return undefined;
}

async function verifyTokenWithBackend(token: string, apiBaseUrl?: string): Promise<StoredExtensionAuth | undefined> {
  try {
    const decoded = decodeJwtPayload(token);
    
    // Validate token audience matches our expected audience
    const tokenAudience = typeof decoded?.aud === "string" ? decoded.aud : undefined;
    if (!tokenAudience || tokenAudience !== AUTH0_AUDIENCE) {
      log.warn("Token audience mismatch", { expected: AUTH0_AUDIENCE, actual: tokenAudience });
      return undefined;
    }
    
    const idTokenClaims = coerceIdTokenClaims(decoded);
    let account = buildAccountFromClaims(decoded);

    const apiBase = resolveApiBaseUrl(apiBaseUrl);

    // Try to fetch user profile with this token to verify it's valid
    const response = await fetch(`${apiBase}/user/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      return undefined;
    }

    let profile: Record<string, unknown> | undefined;
    try {
      profile = (await response.json()) as Record<string, unknown>;
    } catch {
      profile = undefined;
    }

    if (profile) {
      const profileUserId = typeof profile.userId === "string" ? profile.userId : undefined;
      const profileName = typeof profile.username === "string" ? profile.username : undefined;
      if (profileUserId) {
        if (!account) {
          account = {};
        }
        account.userId ??= profileUserId;
        account.subject ??= profileUserId;
      }
      if (profileName) {
        if (!account) {
          account = {};
        }
        account.name ??= profileName;
      }
    }

    const expiresAt = deriveExpiryFromClaims(decoded);
    const scopes = deriveScopesFromClaims(decoded);
    const audience = typeof decoded?.aud === "string" ? decoded.aud : AUTH0_AUDIENCE;

    // Token is valid, create a StoredExtensionAuth object
    return {
      provider: "auth0",
      tokenType: "Bearer",
      accessToken: token,
      refreshToken: undefined,
      scopes,
      expiresAt,
      audience,
      account,
      idTokenClaims,
    };
  } catch (error) {
    log.warn("Failed to verify token:", error);
    return undefined;
  }
}

async function buildAuthFromAuth0Tokens(auth0AccessToken: string, auth0IdToken: string, user: Auth0User, apiBaseUrl?: string): Promise<StoredExtensionAuth> {
  const apiBase = resolveApiBaseUrl(apiBaseUrl);
  // Exchange Auth0 tokens with backend to get extension tokens
  const response = await fetch(`${apiBase}/extensions/auth/token-exchange`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      accessToken: auth0AccessToken,
      idToken: auth0IdToken,
    }),
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    throw new Error(`Failed to exchange Auth0 tokens: ${response.status} ${errorText}`);
  }

  const data = await response.json();

  const exchangeClaims = coerceIdTokenClaims(data?.idTokenClaims);
  const decodedIdClaims = coerceIdTokenClaims(decodeJwtPayload(auth0IdToken));
  const idTokenClaims = mergeIdTokenClaims(decodedIdClaims, exchangeClaims);
  const account = buildAccountFromExchange(data?.user, user, idTokenClaims);

  // The backend returns extension tokens that can be used with the API
  return {
    provider: "auth0",
    tokenType: "Bearer",
    accessToken: data.accessToken,
    refreshToken: data.refreshToken,
    scopes: data.scopes || [],
    expiresAt: Date.now() + (data.expiresIn * 1000),
    refreshExpiresAt: data.refreshExpiresIn ? Date.now() + (data.refreshExpiresIn * 1000) : undefined,
    audience: AUTH0_AUDIENCE,
    idToken: auth0IdToken,
    idTokenClaims,
    account,
  };
}

function coerceIdTokenClaims(raw: unknown): Auth0IdTokenClaims | undefined {
  if (!raw || typeof raw !== "object") {
    return undefined;
  }
  const entries = Object.entries(raw as Record<string, unknown>);
  if (!entries.length) {
    return undefined;
  }
  const claims: Auth0IdTokenClaims = {};
  for (const [key, value] of entries) {
    if (key === "sub" && typeof value === "string") {
      claims.sub = value;
    } else if (key === "email" && typeof value === "string") {
      claims.email = value;
    } else if (key === "name" && typeof value === "string") {
      claims.name = value;
    } else {
      claims[key] = value;
    }
  }
  return claims;
}

function mergeIdTokenClaims(
  primary: Auth0IdTokenClaims | undefined,
  secondary: Auth0IdTokenClaims | undefined
): Auth0IdTokenClaims | undefined {
  if (!primary && !secondary) {
    return undefined;
  }
  return { ...(primary ?? {}), ...(secondary ?? {}) };
}

function buildAccountFromExchange(
  exchangeUser: Auth0User | undefined,
  auth0User: Auth0User | undefined,
  claims: Auth0IdTokenClaims | undefined
): StoredExtensionAuth["account"] {
  const account: NonNullable<StoredExtensionAuth["account"]> = {};

  const exchangeUserId = typeof exchangeUser?.userId === "string" ? exchangeUser.userId : undefined;
  const auth0Subject = typeof auth0User?.sub === "string"
    ? auth0User.sub
    : typeof auth0User?.user_id === "string"
      ? auth0User.user_id
      : undefined;
  const claimsSubject = typeof claims?.sub === "string" ? claims.sub : undefined;

  const userId = exchangeUserId ?? auth0Subject ?? claimsSubject;
  if (userId) {
    account.userId = userId;
  }
  if (claimsSubject ?? auth0Subject) {
    account.subject = claimsSubject ?? auth0Subject;
  }

  const exchangeEmail = typeof exchangeUser?.email === "string" ? exchangeUser.email : undefined;
  const auth0Email = typeof auth0User?.email === "string" ? auth0User.email : undefined;
  const claimsEmail = typeof claims?.email === "string" ? claims.email : undefined;
  const email = exchangeEmail ?? auth0Email ?? claimsEmail;
  if (email) {
    account.email = email;
  }

  const exchangeName = typeof exchangeUser?.name === "string" ? exchangeUser.name : undefined;
  const auth0Name = typeof auth0User?.name === "string" ? auth0User.name : undefined;
  const claimsName = typeof claims?.name === "string" ? claims.name : undefined;
  const name = exchangeName ?? auth0Name ?? claimsName ?? email;
  if (name) {
    account.name = name;
  }

  if (typeof exchangeUser?.isSuperUser === "boolean") {
    account.isSuperUser = exchangeUser.isSuperUser;
  }

  return Object.keys(account).length ? account : undefined;
}


function deriveExpiryFromClaims(claims: Record<string, unknown> | undefined): number {
  if (claims && typeof claims.exp === "number") {
    return claims.exp * 1000;
  }
  return Date.now() + 30 * 24 * 60 * 60 * 1000;
}

function deriveScopesFromClaims(claims: Record<string, unknown> | undefined): string[] {
  if (!claims) {
    return [];
  }
  const scopeValue = (claims as Record<string, unknown>).scope;
  if (Array.isArray(scopeValue)) {
    return scopeValue.filter((value): value is string => typeof value === "string");
  }
  if (typeof scopeValue === "string") {
    return scopeValue
      .split(/\s+/)
      .map((value) => value.trim())
      .filter((value) => value.length > 0);
  }
  return [];
}

function decodeJwtPayload(token: string | undefined): Record<string, unknown> | undefined {
  if (!token || typeof token !== "string") {
    return undefined;
  }
  const segments = token.split(".");
  if (segments.length < 2) {
    return undefined;
  }
  try {
    const base64 = segments[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const pad = base64.length % 4;
    const normalized = pad ? base64 + "=".repeat(4 - pad) : base64;
    const decoded = atob(normalized);
    return JSON.parse(decoded) as Record<string, unknown>;
  } catch (error) {
    log.warn("Failed to decode JWT payload", error);
    return undefined;
  }
}

export async function checkAuthStatus(apiBaseUrl?: string): Promise<StoredExtensionAuth | undefined> {
  ensureBrowserEnvironment();

  // First, check if there's a token in the cookie (shared with the dashboard)
  const cookieToken = readCookie(TOKEN_COOKIE_NAME);
  if (cookieToken) {
    const verified = await verifyTokenWithBackend(cookieToken, apiBaseUrl);
    if (verified) {
      persistAuth(verified);
      return verified;
    }
  }

  // Check sessionStorage for persisted auth
  const stored = loadPersistedAuth();
  if (stored && stored.expiresAt && Date.now() < stored.expiresAt) {
    return stored;
  }

  try {
    const client = await getAuth0Client();
    
    // Check if user is authenticated with Auth0 SPA SDK
    const isAuthenticated = await client.isAuthenticated();
    
    if (!isAuthenticated) {
      // Check if we're returning from a login redirect
      const query = window.location.search;
      if (query.includes("code=") && query.includes("state=")) {
        try {
          await client.handleRedirectCallback();
          // After handling callback, check auth again
          const nowAuthenticated = await client.isAuthenticated();
          if (nowAuthenticated) {
            return await buildStoredAuthFromClient(client, apiBaseUrl);
          }
        } catch (error) {
          log.error("Failed to handle redirect callback:", error);
          return undefined;
        }
      }
      return undefined;
    }

    return await buildStoredAuthFromClient(client, apiBaseUrl);
  } catch (error) {
    log.warn("Failed to check auth status:", error);
    return undefined;
  }
}

async function buildStoredAuthFromClient(client: Auth0Client, apiBaseUrl?: string): Promise<StoredExtensionAuth> {
  const accessToken = await client.getTokenSilently();
  const user = (await client.getUser()) ?? ({} as Auth0User);
  const idTokenClaims = await client.getIdTokenClaims();
  
  if (!accessToken) {
    throw new Error("Failed to get access token from Auth0");
  }

  const idToken = idTokenClaims?.__raw;
  if (!idToken) {
    throw new Error("Failed to get ID token from Auth0");
  }

  // Use Auth0 tokens directly - they are valid for our API
  const auth = await buildAuthFromAuth0Tokens(accessToken, idToken, user as Auth0User, apiBaseUrl);
  persistAuth(auth);
  return auth;
}

export async function login(): Promise<StoredExtensionAuth> {
  ensureBrowserEnvironment();

  try {
    const client = await getAuth0Client();
    
    // Always redirect to the portal dashboard after login
    // This ensures users end up at tracker.lotlingo.com/portal regardless of where they started
    const redirectUri = window.location.origin + "/portal";
    
    // Redirect to Auth0 login page
    await client.loginWithRedirect({
      authorizationParams: {
        redirect_uri: redirectUri,
        audience: AUTH0_AUDIENCE,
        scope: AUTH0_SCOPE,
      },
    });
    
    // This will never resolve because we're redirecting away
    return new Promise(() => {});
  } catch (error) {
    log.error("Failed to initiate login:", error);
    throw new Error("Failed to start authentication. Please try again later.");
  }
}

export async function logout(returnTo?: string): Promise<void> {
  ensureBrowserEnvironment();

  const resolvedReturnTo =
    typeof returnTo === "string" && returnTo.trim().length
      ? returnTo.trim()
      : window.location.origin + "/portal";

  try {
    const client = await getAuth0Client();
    clearPersistedAuth();

    await client.logout({
      logoutParams: {
        returnTo: resolvedReturnTo,
      },
    });
  } catch (error) {
    log.warn("Failed to logout:", error);
    // Still clear stored auth even if Auth0 logout fails
    clearPersistedAuth();
    window.location.href = resolvedReturnTo;
  }
}

/**
 * Handle auth callback - this is called automatically by checkAuthStatus
 * when it detects a redirect callback from Auth0
 */
export function handleAuthCallback(): void {
  // This is now handled automatically in checkAuthStatus()
  // Keeping this function for backward compatibility
}
